import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, MapPin, Sparkles } from "lucide-react";
import { IcelandMap } from "./IcelandMap";
import { BeltModal } from "./BeltModal";
import { BELTS } from "../../data/belts";
import { computeJourney, beltForWeek, formatLongDate } from "../../lib/journey";

export const MapExperience = () => {
  // Refresh once per day at midnight so the avatar advances.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const next = new Date();
    next.setHours(0, 0, 0, 0);
    next.setDate(next.getDate() + 1);
    const t = setTimeout(() => setNow(new Date()), next - new Date());
    return () => clearTimeout(t);
  }, [now]);

  const journey = useMemo(() => computeJourney(now), [now]);
  const currentBelt = beltForWeek(BELTS, journey.currentWeek);
  const [activeId, setActiveId] = useState(null);

  const openBelt = (id) => setActiveId(id);
  const closeBelt = () => setActiveId(null);

  return (
    <div
      data-testid="map-experience"
      className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-8 lg:py-12"
    >
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#21b766]/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.22em] text-[#21b766]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#21b766]" />
            Taktu áskoruninni
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#121212]">
            Your training journey across <span className="text-[#21b766]">Iceland.</span>
          </h1>
          <p className="mt-2 text-lg sm:text-xl font-medium text-[#52525B]">
            The Sensei Sandra challenge.
          </p>
          <p className="mt-4 text-base text-[#52525B] max-w-2xl">
            Tap any belt on the map to see the main steps you{`'`}ve achieved at
            that stage. Every day, your marker walks a little further, with Sandra
            a few steps ahead, guiding the way.
          </p>
        </div>

        {/* Live "today" panel */}
        <div
          data-testid="today-panel"
          className="flex-shrink-0 rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm min-w-[260px]"
        >
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[#52525B]">
            <Calendar size={12} />
            Today {formatLongDate(now)}
          </div>
          <div className="mt-2 flex items-end gap-3">
            <div>
              <div className="text-3xl font-medium tracking-tight text-[#121212]">
                Week {journey.currentWeek}
                <span className="text-[#52525B] text-lg font-medium"> / 26</span>
              </div>
              <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#21b766] mt-0.5">
                Day {journey.elapsedDays} of {journey.totalDays}
              </div>
            </div>
            <div className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#F15D4E] px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white">
              <Sparkles size={11} />
              {journey.daysRemaining}d left
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 rounded-full bg-zinc-100 overflow-hidden">
            <motion.div
              className="h-full bg-[#21b766]"
              initial={{ width: 0 }}
              animate={{ width: `${journey.progress * 100}%` }}
              transition={{ duration: 1.2, ease: [0.2, 0.7, 0.2, 1] }}
            />
          </div>
          <div className="mt-2 flex items-center gap-1 text-[10px] font-medium text-[#52525B]">
            <MapPin size={11} className="text-[#21b766]" />
            You{`'`}re on the {currentBelt.name.toLowerCase()} stretch.
          </div>
        </div>
      </header>

      {/* Full-width map */}
      <div className="mt-10">
        <IcelandMap
          activeId={activeId}
          onSelect={openBelt}
          progress={journey.progress}
          currentWeek={journey.currentWeek}
          totalDays={journey.totalDays}
        />
      </div>

      {/* Modal */}
      <BeltModal activeId={activeId} onClose={closeBelt} />
    </div>
  );
};
